import { PurchaseEntity } from "@citychallenge/api-public";
import { GameModel } from "../../models";
import { AuthService } from "../auth";
import { UnauthenticatedError } from "../../errors";
import { LoggerService } from "../logger";
import { ApiService } from "../api";
import { Subject, Subscription } from "rxjs";
import { LocalizedError } from "../../errors";
import { ServiceInterface } from "../service.interface";
import { TransTokens } from "../../i18n/trans-tokens";
import { createContext } from "react";
import { IAPAdapterInterface } from "./iap-adapter.interface";

const tokens = TransTokens.create({
  couponNotFound: "This coupon code is invalid.",
  couponUsed: "This coupon code has already been used.",
  alreadyOwned:
    "You already own the challenge for which this coupon code can be used!",
  success:
    "You just succesfully redeemed your access code! You are now the proud owner of '{{game}}'.",
});

export const PurchaseServiceContext = createContext<PurchaseService>(
  null as any
);

export class PurchaseService implements ServiceInterface {
  public context = PurchaseServiceContext;

  private purchaseSubject = new Subject<PurchaseEntity>();

  /**
   * Observable that streams newly made purchases.
   */
  public readonly purchase$ = this.purchaseSubject.asObservable();

  private subscriptions: Subscription[] = [];

  constructor(
    private readonly apiService: ApiService,
    private readonly logger: LoggerService,
    private readonly authService: AuthService,
    private readonly iapAdapter?: IAPAdapterInterface
  ) {}

  public serviceWillMount() {
    this.iapAdapter?.serviceWillMount(this);
  }

  public serviceWillUnmount() {
    this.iapAdapter?.serviceWillUnmount();

    if (this.subscriptions)
      this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public deliverReceipt = (receipt: any) => {
    return this.apiService.clientPrivate
      .deliverPurchases(receipt)
      .then((response) => response.data)
      .then(this.processDeliveredProducts);
  };

  public async getAllPurchases() {
    const purchases = await this.apiService.clientPrivate.allPurchases();
    return purchases.data;
  }

  public async getPurchasesForGame(game: GameModel) {
    const purchases = await this.apiService.clientPrivate.purchasesForGame(
      game.id
    );
    return purchases.data;
  }

  private processDeliveredProducts = (purchases: PurchaseEntity[]) => {
    for (const purchase of purchases) {
      this.logger.captureObject("Delivered purchase", purchase, "Info");
      this.purchaseSubject.next(purchase);
    }
  };

  public async hydrateProducts(games: GameModel[]) {
    try {
      await this.iapAdapter?.hydrateProducts(games);
    } catch (e) {
      // Temporary fix to prevent iOS 14 sim from crashing
      this.logger.captureException(e);

      if (!__DEV__) {
        // Do throw the error in production
        throw e;
      }
    }
  }

  public async restorePurchases() {
    try {
      this.iapAdapter?.restorePurchases();
    } catch (err) {
      this.logger.capture(err);
      throw err;
    }
  }

  public async redeemCoupon(id: string) {
    if (!this.authService.isLoggedIn) {
      throw new UnauthenticatedError();
    }

    try {
      const { data } = await this.apiService.clientPrivate.redeemCoupon({
        id,
      });

      this.processDeliveredProducts([data]);

      return data;
    } catch (e) {
      const { response } = e;
      if (response) {
        if (response.status === 404) {
          throw new LocalizedError(e, tokens.couponNotFound);
        }

        const message = response.data?.message || "";
        if (message === "COUPON_ALREADY_USED") {
          throw new LocalizedError(e, tokens.couponUsed);
        }

        if (message === "ITEM_ALREADY_OWNED") {
          throw new LocalizedError(e, tokens.alreadyOwned);
        }
      }
      throw new LocalizedError(e);
    }
  }

  public async buy(game: GameModel) {
    if (!this.authService.isLoggedIn) {
      throw new UnauthenticatedError();
    }

    try {
      await this.iapAdapter?.startPurchase(game);
    } catch (err) {
      this.logger.capture(err, "Error", (scope) => {
        scope.setExtra("code", err.code);
        scope.setExtra("message", err.message);
      });
    }

    return true;
  }
}
